<script setup lang="ts">
// GA4 override indicator (ORP-25550 / ORP-25673) — ports the v3 label + orange dot button
// (`CustomDashboardScoreCards.vue`, `Ga4LegendTooltip.vue`) and its explanation tooltip. The
// default slot is the label the dot belongs to; hover, focus or a click open the explanation.
import { useI18n } from '@/shared/i18n'
import { Tooltip } from '@/shared/ui/tooltip'

withDefaults(
  defineProps<{
    /** Name of what the dot marks — the metric for a score card, the series list for a chart. */
    label: string
    /** `scorecard` explains a GA4 total; `chart` explains GA4 daily values. */
    variant?: 'chart' | 'scorecard'
  }>(),
  { variant: 'chart' },
)

const { t } = useI18n()

function focusTrigger(event: MouseEvent): void {
  ;(event.currentTarget as HTMLElement | null)?.focus()
}
</script>

<template>
  <Tooltip placement="top">
    <button
      type="button"
      class="ga4-indicator inline-flex min-w-0 cursor-pointer items-start gap-2xs rounded-sm border-0 bg-transparent p-0 text-left text-inherit [font:inherit] focus-visible:outline-solid focus-visible:outline-[length:var(--ui-ring-width)] focus-visible:outline-offset-2 focus-visible:outline-ring"
      :aria-label="t('reports.ga4.ariaLabel', { label })"
      @click.stop="focusTrigger"
    >
      <slot />
      <span
        aria-hidden="true"
        class="ga4-indicator__dot -ml-0.5 mt-0.5 size-1.5 shrink-0 rounded-full bg-accent"
      />
    </button>
    <template #content>
      <span class="ga4-indicator__tooltip block max-w-64 text-left">
        <span class="mb-1.5 block text-sm font-semibold">{{ t('reports.ga4.title') }}</span>
        <template v-if="variant === 'scorecard'">
          <span class="mb-1.5 block">{{ t('reports.ga4.scorecardBody') }}</span>
          <span class="block">{{ t('reports.ga4.scorecardSuffix') }}</span>
        </template>
        <span v-else class="block">{{ t('reports.ga4.body') }}</span>
      </span>
    </template>
  </Tooltip>
</template>
